import { useState, useEffect, useRef } from 'react';
import type { WidgetConfig } from '../types';

const configCache = new Map<string, WidgetConfig>();

// Clear cache on HMR to avoid stale config in development
if (import.meta.hot) {
  import.meta.hot.dispose(() => configCache.clear());
}

function getApiBase(): string {
  return import.meta.env.VITE_API_URL || '';
}

export function useConfig(tenant: string | null) {
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!tenant) {
      setLoading(false);
      setError('No tenant specified');
      return;
    }

    if (configCache.has(tenant)) {
      setConfig(configCache.get(tenant)!);
      setLoading(false);
      return;
    }

    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const apiBase = getApiBase();

    fetch(`${apiBase}/api/v1/widget-config/${encodeURIComponent(tenant)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Config fetch failed: ${r.status}`);
        return r.json();
      })
      .then((data: { tenant_id: string; tenant_slug: string; config: WidgetConfig }) => {
        configCache.set(tenant, data.config);
        setConfig(data.config);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load config');
        setLoading(false);
      });
  }, [tenant]);

  return { config, loading, error };
}
